export const CALENDLY_VISIBLE_MONTHS = 6;

export const getCalendarMonth = (date: Date, timeZone = "UTC"): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  return `${parts.find(({ type }) => type === "year")?.value}-${parts.find(({ type }) => type === "month")?.value}`;
};

export const shiftCalendarMonth = (month: string, offset: number): string => {
  const date = new Date(`${month}-01T00:00:00.000Z`);
  date.setUTCMonth(date.getUTCMonth() + offset);
  return date.toISOString().slice(0, 7);
};

export const getVisibleCalendarMonths = (now: Date, timeZone = "UTC") =>
  Array.from({ length: CALENDLY_VISIBLE_MONTHS }, (_, index) =>
    shiftCalendarMonth(getCalendarMonth(now, timeZone), index),
  );

const monthStartInTimeZone = (month: string, timeZone: string): Date => {
  const target = Date.parse(`${month}-01T00:00:00.000Z`);
  let instant = target;
  let previousInstant: number | undefined;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  for (let attempt = 0; attempt < 4; attempt++) {
    const parts = Object.fromEntries(
      formatter
        .formatToParts(new Date(instant))
        .map(({ type, value }) => [type, value]),
    );
    const represented = Date.parse(
      `${parts["year"]}-${parts["month"]}-${parts["day"]}T${parts["hour"]}:${parts["minute"]}:${parts["second"]}.000Z`,
    );
    const difference = target - represented;
    if (difference === 0) break;
    const nextInstant = instant + difference;
    // Midnight can be skipped by a DST transition on the first of the month.
    if (nextInstant === previousInstant)
      return new Date(Math.max(instant, nextInstant));
    previousInstant = instant;
    instant = nextInstant;
  }
  return new Date(instant);
};

export const getCalendarMonthWindow = (month: string, timeZone = "UTC") => ({
  startTime: monthStartInTimeZone(month, timeZone),
  endTime: monthStartInTimeZone(shiftCalendarMonth(month, 1), timeZone),
});

export const getUtcMonthsForWindow = (
  startTime: Date,
  endTime: Date,
): string[] => {
  if (startTime >= endTime) return [];
  const result: string[] = [];
  for (
    let month = getCalendarMonth(startTime);
    month <= getCalendarMonth(new Date(endTime.getTime() - 1));
    month = shiftCalendarMonth(month, 1)
  )
    result.push(month);
  return result;
};
